import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  portalWiDaywork, portalWiDayworkList, apiErrText, countdown, beijingToday,
  DAYWORK_STATE,
  type PortalWiDetail, type PortalWiDayworkList, type PortalWiDayworkRecord,
  type DayworkPerson, type DayworkMaterial, type DayworkEquipment,
} from '../lib/api';
import { PhotoPicker } from './PortalQuoteForm';
import { toast } from './ui/Toast';

/**
 * 9.2.11 计日工日报 — one day, one record. Eric 9/18 (工程指令).
 *
 * The clause mandates six things per day (事由 · 工作内容/数量 · 人员 姓名/工种/级别/工时 · 材料 · 机械 ·
 * 其他凭证), and G14 gives them 48 hours from the end of the工作日 to file it. After that the day is
 * irreversibly 失权 — so this form's entire job is to be faster to fill in than to postpone:
 *   · today (Beijing) is pre-selected and a future day cannot be chosen;
 *   · every filed day is listed with its own countdown to the 48-hour bar, coloured at 40 / 24 / 12 h;
 *   · 暂存 (`?draft=1`) exists so a foreman can start the record at lunch and finish it at knock-off —
 *     the sweep still forfeits an abandoned draft past the bar, and the list says so;
 *   · a LATE filing is not refused. The server answers 201 with `forfeited:true` and the clause, and this
 *     form shows that as an OUTCOME, not as an error — the day is on record, unpayable but visible.
 *
 * Money-free by construction: the labor projection of a daywork record carries no priced_amount_rmb and
 * no 核实 note, and nothing here asks for one.
 */

/** Colour steps on the 48-hour bar: the plan's reminder hours (12 / 24 / 40) read as a traffic light. */
function barTone(hoursLeft: number | null | undefined): string {
  if (hoursLeft == null) return 'pill-muted';
  if (hoursLeft <= 0) return 'pill-bad';
  if (hoursLeft <= 12) return 'pill-bad';
  if (hoursLeft <= 24) return 'pill-warn';
  if (hoursLeft <= 40) return 'pill-stage';
  return 'pill-muted';
}
/** hours to the bar, from whichever the payload gave us — PR5's derived figure, else `hard_bar_at`. */
function hoursToBar(r: PortalWiDayworkRecord, now: number): number | null {
  if (typeof r.hours_left === 'number') return r.hours_left;
  if (!r.hard_bar_at) return null;
  const t = Date.parse(r.hard_bar_at);
  return Number.isFinite(t) ? (t - now) / 3_600_000 : null;
}

const emptyPerson = (): DayworkPerson => ({ name: '', trade: '', grade: '', hours: '' });
const emptyMaterial = (): DayworkMaterial => ({ name: '', category: '', spec: '', brand: '', qty: '', unit: '' });
const emptyEquipment = (): DayworkEquipment => ({ model: '', qty: '', shifts: '' });

export default function PortalDayworkForm({
  eid, detail, onUpdated,
}: {
  eid: string;
  detail: PortalWiDetail;
  onUpdated: () => void;
}) {
  const wi = detail.instruction;
  const qc = useQueryClient();
  const today = beijingToday();

  // PR5's own read: it carries the derived clocks, the photos per day, the 48-hour clause and the
  // 固定总价 banner. The PR4a detail payload also carries `daywork_records`, so the list still renders
  // (without the derived flags) if this read has not landed yet.
  const { data: dw } = useQuery<PortalWiDayworkList>({
    queryKey: ['portal-daywork', eid, wi.id],
    queryFn: () => portalWiDayworkList(eid, wi.id),
    refetchInterval: 60_000,
  });
  const records: PortalWiDayworkRecord[] = dw?.records ?? detail.daywork_records ?? [];

  const [workDate, setWorkDate] = useState(today);
  const [reason, setReason] = useState('');
  const [content, setContent] = useState('');
  const [qty, setQty] = useState('');
  const [persons, setPersons] = useState<DayworkPerson[]>([emptyPerson()]);
  const [materials, setMaterials] = useState<DayworkMaterial[]>([]);
  const [equipment, setEquipment] = useState<DayworkEquipment[]>([]);
  const [vouchers, setVouchers] = useState('');
  const [picked, setPicked] = useState<File[]>([]);
  const [busy, setBusy] = useState<'draft' | 'submit' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** A 201 that came back forfeited — an outcome to display, never a toast that scrolls away. */
  const [lost, setLost] = useState<{ work_date: string; contract_text: string } | null>(null);

  const existing = useMemo(() => records.find((r) => r.work_date === workDate), [records, workDate]);
  const readOnly = existing?.state === 'verified' || existing?.state === 'forfeited';

  /** Switching to a day that already has a record loads it, so 暂存 → finish later actually works. */
  useEffect(() => {
    if (!existing) {
      // review 9/18 (PR9): a day with no record starts BLANK — the previous day's 事由/人员 must not ride along
      setReason(''); setContent(''); setQty(''); setPersons([emptyPerson()]); setMaterials([]); setEquipment([]); setVouchers(''); setPicked([]); setErr(null);
      return;
    }
    setReason(existing.reason_md ?? '');
    setContent(existing.work_content_md ?? '');
    setQty(existing.work_qty_md ?? '');
    setPersons(existing.persons?.length ? existing.persons.map((p) => ({ ...p })) : [emptyPerson()]);
    setMaterials((existing.materials ?? []).map((m) => ({ ...m })));
    setEquipment((existing.equipment ?? []).map((q) => ({ ...q })));
    setVouchers(existing.other_vouchers_md ?? '');
    setPicked([]);
    setErr(null);
  }, [existing?.id, existing?.updated_at]);   // eslint-disable-line react-hooks/exhaustive-deps

  // review 9/18 (PR9): the server refuses if ANY named row lacks 工时 — mirror it, and name the row
  const named = persons.filter((p) => String(p.name ?? '').trim());
  const badHours = named.filter((p) => !(Number(p.hours) > 0));
  const personsOk = named.length > 0 && badHours.length === 0;
  const photosOnFile = existing?.photos?.length ?? 0;
  const photoRequired = (dw as { photo_required_on_daywork?: boolean } | undefined)?.photo_required_on_daywork ?? true;
  const photoOwed = photoRequired && picked.length === 0 && photosOnFile === 0;

  async function send(draft: boolean) {
    setBusy(draft ? 'draft' : 'submit'); setErr(null); setLost(null);
    try {
      const res = await portalWiDaywork(eid, wi.id, {
        work_date: workDate,
        reason_md: reason.trim() || undefined,
        work_content_md: content.trim() || undefined,
        work_qty_md: qty.trim() || undefined,
        // 9.2.11 wants all four per person; blank optional cells are dropped rather than sent as ''
        persons_json: JSON.stringify(
          persons
            .filter((p) => String(p.name ?? '').trim())
            .map((p) => ({ name: String(p.name).trim(), trade: p.trade || undefined, grade: p.grade || undefined, hours: Number(p.hours) })),
        ),
        materials_json: JSON.stringify(materials.filter((m) => String(m.name ?? '').trim())),
        equipment_json: JSON.stringify(equipment.filter((q) => String(q.model ?? '').trim())),
        other_vouchers_md: vouchers.trim() || undefined,
      }, picked, draft);

      setPicked([]);
      void qc.invalidateQueries({ queryKey: ['portal-daywork', eid, wi.id] });
      void qc.invalidateQueries({ queryKey: ['portal-todo-count'] });
      onUpdated();

      if (res.forfeited) {
        // NOT an error: the day IS on record. The clause is the only place they are told it is unpayable.
        setLost({ work_date: workDate, contract_text: res.contract_text ?? DAYWORK_STATE.forfeited.note ?? '' });
      } else {
        toast(draft ? `${workDate} 已暂存，记得在 48 小时内上报。/ Saved as a draft — file it within 48 hours.`
          : `${workDate} 日报已上报，等待核实。/ Filed — WTL will verify it.`, { kind: 'success' });
      }
    } catch (e) {
      setErr(apiErrText(e));
    } finally { setBusy(null); }
  }

  const now = Date.now();
  return (
    <div className="card" style={{ marginTop: 10, borderLeft: '3px solid var(--accent)' }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
        <strong style={{ flex: 1, minWidth: 140 }}>计日工日报 / Daywork daily record</strong>
        {wi.daywork_category && <span className="pill small pill-muted">{wi.daywork_category}</span>}
      </div>

      {/* G11 — 固定总价 has no 计日工 clause at all; say so before anything is filled in. */}
      {dw?.banner_zh && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>
          ⚠ {dw.banner_zh}
          <div className="muted">This contract has no daywork clause — a supplementary agreement must fix the basis of pricing.</div>
        </div>
      )}

      <div className="muted small" style={{ marginTop: 4 }}>
        合同 9.2.11：{dw?.contract_text_zh ?? '如48小时未上报，视为不产生费用，后续补报不予认可'}。
        每个工作日一条，含事由、工作内容与数量、人员（姓名/工种/级别/工时）、材料、机械、其他凭证与现场照片。
        <div>Clause 9.2.11 — one record per working day, filed within 48 hours of the day ending.</div>
      </div>

      {/* ── 已填日报 / the ledger, each with its own countdown to the 48-hour bar ─────────────────── */}
      <div style={{ marginTop: 8 }}>
        <div className="small"><strong>已填日报 / Filed days（{records.length}）</strong></div>
        {records.length === 0 && <div className="muted small">还没有日报。/ Nothing filed yet.</div>}
        {records.map((r) => {
          const st = DAYWORK_STATE[r.state] ?? DAYWORK_STATE.open;
          const h = hoursToBar(r, now);
          const cd = countdown(r.hard_bar_at, now);
          const live = r.state === 'open' || r.state === 'submitted';
          return (
            <div key={r.id} className="row-wrap" style={{ gap: 6, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid var(--rule-hair)' }}>
              <button
                className="small" style={{ minWidth: 104 }}
                onClick={() => setWorkDate(r.work_date)}
                title="打开这一天 / open this day"
              >{r.work_date}</button>
              <span className={`pill small ${st.pill}`}>{st.label}</span>
              {r.state === 'open' && h != null && (
                <span className={`pill small ${barTone(h)}`} title="距 48 小时上报期限 / to the 48-hour bar">{cd.text}</span>
              )}
              {r.state === 'submitted' && h != null && h > 0 && <span className="muted small">{cd.text}</span>}
              {(r.photos?.length ?? 0) > 0 && <span className="muted small">📷 {r.photos!.length}</span>}
              {live && (r.persons?.length ?? 0) > 0 && <span className="muted small">{r.persons!.length} 人</span>}
              {r.state === 'forfeited' && (
                <span className="small" style={{ color: 'var(--bad)', flexBasis: '100%' }}>
                  {r.contract_text_zh ?? DAYWORK_STATE.forfeited.note}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── a 201 that came back forfeited — shown as an outcome, prominently, not as a toast ────── */}
      {lost && (
        <div className="card" style={{ marginTop: 10, borderLeft: '3px solid var(--bad)', background: 'var(--bad-soft)' }}>
          <div className="small" style={{ color: 'var(--bad)' }}>
            <strong>⛔ {lost.work_date} 已超过 48 小时上报期限 / filed past the 48-hour bar</strong>
          </div>
          <div className="small" style={{ marginTop: 4 }}>合同 9.2.11：{lost.contract_text}</div>
          <div className="muted small" style={{ marginTop: 4 }}>
            记录已保存并可查阅，但按合同不计费用。如有异议请在「沟通」留言联系 WTL；以合同约定为准。/ The record
            is stored and visible, but the contract does not price it. Raise it with WTL in the thread.
          </div>
        </div>
      )}

      {/* ── 填写 / the form ─────────────────────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 10, borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
        <div className="row-wrap" style={{ gap: 6, alignItems: 'center' }}>
          <label className="small" htmlFor="dw-date">施工日期 / Work date</label>
          <input
            id="dw-date" type="date" value={workDate} max={today}
            onChange={(e) => setWorkDate(e.target.value)} disabled={!!busy}
          />
          {existing && <span className={`pill small ${(DAYWORK_STATE[existing.state] ?? DAYWORK_STATE.open).pill}`}>{(DAYWORK_STATE[existing.state] ?? DAYWORK_STATE.open).label}</span>}
        </div>
        <div className="muted small" style={{ marginTop: 2 }}>
          默认今天（北京时间），不能选择将来的日期；同一天只能有一条记录，再次提交即为修改。/ Beijing today by
          default; no future dates; one record per day — filing again edits it.
        </div>

        {readOnly ? (
          <div className="small" style={{ marginTop: 8, color: existing!.state === 'forfeited' ? 'var(--bad)' : 'var(--good)' }}>
            {existing!.state === 'forfeited'
              ? <>⛔ {existing!.contract_text_zh ?? DAYWORK_STATE.forfeited.note} 该日记录只读。/ This day is time-barred and read-only.</>
              : <>✓ 该日已核实确认，记录只读。/ This day has been verified — the record is read-only.</>}
            <DayworkReadOnly record={existing!} />
          </div>
        ) : (
          <>
            {/* ① 事由 */}
            <Field label="① 事由 / Reason" hint="为什么发生这项零星用工（谁指示、依据什么）/ why this daywork happened">
              <textarea rows={2} className="grow" style={{ width: '100%' }} value={reason} disabled={!!busy}
                onChange={(e) => setReason(e.target.value)} placeholder="如：甲方现场口头指示拆除原隔墙，工程师 X 到场确认" />
            </Field>
            {/* ② 工作内容 / 数量 */}
            <Field label="② 工作内容 / Work done">
              <textarea rows={2} className="grow" style={{ width: '100%' }} value={content} disabled={!!busy}
                onChange={(e) => setContent(e.target.value)} placeholder="当日实际完成的工作 / what was actually done" />
            </Field>
            <Field label="② 工作数量 / Quantity">
              <input className="grow" style={{ width: '100%' }} value={qty} disabled={!!busy}
                onChange={(e) => setQty(e.target.value)} placeholder="如：拆除隔墙 26㎡ / e.g. 26 m² demolished" />
            </Field>

            {/* ③ 人员 — the one group the clause makes decisive */}
            <Field label="③ 人员（必填）/ Crew (required)" hint="9.2.11：每一个人的姓名、工种、等级、工时 / name, trade, grade, hours for every person">
              {persons.map((p, i) => (
                <div key={i} className="row-wrap" style={{ gap: 4, alignItems: 'center', marginTop: 4 }}>
                  <input style={{ flex: '1 1 92px', minWidth: 80 }} value={p.name} disabled={!!busy} placeholder="姓名 / name"
                    onChange={(e) => setPersons(persons.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  <input style={{ flex: '1 1 80px', minWidth: 70 }} value={p.trade ?? ''} disabled={!!busy} placeholder="工种 / trade"
                    onChange={(e) => setPersons(persons.map((x, j) => (j === i ? { ...x, trade: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 70px', minWidth: 60 }} value={p.grade ?? ''} disabled={!!busy} placeholder="级别 / grade"
                    onChange={(e) => setPersons(persons.map((x, j) => (j === i ? { ...x, grade: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 70px', minWidth: 60 }} type="number" inputMode="decimal" min={0} step="0.5"
                    value={p.hours as string | number} disabled={!!busy} placeholder="工时 / h"
                    onChange={(e) => setPersons(persons.map((x, j) => (j === i ? { ...x, hours: e.target.value } : x)))} />
                  {persons.length > 1 && (
                    <button className="small" disabled={!!busy} onClick={() => setPersons(persons.filter((_, j) => j !== i))} aria-label="删除该行 / remove row">✕</button>
                  )}
                </div>
              ))}
              <button className="small" style={{ marginTop: 4 }} disabled={!!busy} onClick={() => setPersons([...persons, emptyPerson()])}>＋ 增加人员 / Add a person</button>
              {!personsOk && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>至少一名人员需填写姓名与工时。/ At least one person needs a name and hours.</div>}
            </Field>

            {/* ④ 材料 */}
            <Field label="④ 材料 / Materials">
              {materials.map((m, i) => (
                <div key={i} className="row-wrap" style={{ gap: 4, alignItems: 'center', marginTop: 4 }}>
                  <input style={{ flex: '1 1 92px', minWidth: 80 }} value={m.name} disabled={!!busy} placeholder="名称 / name"
                    onChange={(e) => setMaterials(materials.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 76px', minWidth: 66 }} value={m.category ?? ''} disabled={!!busy} placeholder="类别 / cat."
                    onChange={(e) => setMaterials(materials.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))} />
                  <input style={{ flex: '1 1 80px', minWidth: 70 }} value={m.spec ?? ''} disabled={!!busy} placeholder="规格 / spec"
                    onChange={(e) => setMaterials(materials.map((x, j) => (j === i ? { ...x, spec: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 76px', minWidth: 66 }} value={m.brand ?? ''} disabled={!!busy} placeholder="品牌 / brand"
                    onChange={(e) => setMaterials(materials.map((x, j) => (j === i ? { ...x, brand: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 62px', minWidth: 56 }} type="number" inputMode="decimal" min={0} step="0.01" value={m.qty as string | number} disabled={!!busy} placeholder="数量 / qty"
                    onChange={(e) => setMaterials(materials.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 56px', minWidth: 50 }} value={m.unit ?? ''} disabled={!!busy} placeholder="单位 / unit"
                    onChange={(e) => setMaterials(materials.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)))} />
                  <button className="small" disabled={!!busy} onClick={() => setMaterials(materials.filter((_, j) => j !== i))} aria-label="删除该行 / remove row">✕</button>
                </div>
              ))}
              <button className="small" style={{ marginTop: 4 }} disabled={!!busy} onClick={() => setMaterials([...materials, emptyMaterial()])}>＋ 增加材料 / Add a material</button>
            </Field>

            {/* ⑤ 机械 */}
            <Field label="⑤ 机械 / Equipment">
              {equipment.map((q, i) => (
                <div key={i} className="row-wrap" style={{ gap: 4, alignItems: 'center', marginTop: 4 }}>
                  <input style={{ flex: '1 1 110px', minWidth: 90 }} value={q.model} disabled={!!busy} placeholder="型号 / model"
                    onChange={(e) => setEquipment(equipment.map((x, j) => (j === i ? { ...x, model: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 62px', minWidth: 56 }} type="number" inputMode="decimal" min={0} step="1" value={q.qty as string | number} disabled={!!busy} placeholder="数量 / qty"
                    onChange={(e) => setEquipment(equipment.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                  <input style={{ flex: '0 1 70px', minWidth: 62 }} type="number" inputMode="decimal" min={0} step="0.5" value={q.shifts as string | number} disabled={!!busy} placeholder="台班 / shifts"
                    onChange={(e) => setEquipment(equipment.map((x, j) => (j === i ? { ...x, shifts: e.target.value } : x)))} />
                  <button className="small" disabled={!!busy} onClick={() => setEquipment(equipment.filter((_, j) => j !== i))} aria-label="删除该行 / remove row">✕</button>
                </div>
              ))}
              <button className="small" style={{ marginTop: 4 }} disabled={!!busy} onClick={() => setEquipment([...equipment, emptyEquipment()])}>＋ 增加机械 / Add equipment</button>
            </Field>

            {/* ⑥ 其他凭证 + 现场照片 */}
            <Field label="⑥ 其他凭证 / Other vouchers">
              <input className="grow" style={{ width: '100%' }} value={vouchers} disabled={!!busy}
                onChange={(e) => setVouchers(e.target.value)} placeholder="签证单号、过磅单、租赁单等 / ticket numbers, weighbridge slips, hire notes" />
            </Field>
            <Field label={photoRequired ? '现场照片（必填）/ Site photos (required)' : '现场照片 / Site photos'}>
              {photosOnFile > 0 && <span className="pill small pill-good">已有 {photosOnFile} 张 / {photosOnFile} on file</span>}
              <PhotoPicker
                picked={picked} onPicked={setPicked} disabled={!!busy} idPrefix={`dw-${wi.id}`}
                hint="请拍摄当日作业面与出工人员，作为实际消耗的核实依据。/ Photograph the work face and the crew — this is what WTL verifies against."
              />
              {photoOwed && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>上报前请先附现场照片。/ Attach a site photo before filing.</div>}
            </Field>

            {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}

            <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
              <button disabled={!!busy} onClick={() => void send(true)}>
                {busy === 'draft' ? '暂存中… / saving…' : '💾 暂存 / Save draft'}
              </button>
              {badHours.length > 0 && <div className="small" style={{ color: 'var(--bad)' }}>{badHours.map((p) => p.name).join('、')} 缺少工时 / missing hours</div>}
              <button className="btn-good" disabled={!!busy || !personsOk || photoOwed} onClick={() => void send(false)}>
                {busy === 'submit' ? '上报中… / filing…' : '✓ 上报 / File the day'}
              </button>
            </div>
            <div className="muted small" style={{ marginTop: 4 }}>
              暂存不算上报，超过 48 小时仍会失权。/ A draft is not a filing — the 48-hour bar still applies to it.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** One labelled group of the 9.2.11 form. Kept inline so the six groups read in order in the source. */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div className="small"><strong>{label}</strong></div>
      {hint && <div className="muted small">{hint}</div>}
      {children}
    </div>
  );
}

/** A verified or time-barred day, as filed. Read-only on purpose — the record is evidence now. */
function DayworkReadOnly({ record }: { record: PortalWiDayworkRecord }) {
  return (
    <div className="muted small" style={{ marginTop: 6, color: 'var(--muted)' }}>
      {record.reason_md && <div>① 事由 / reason：{record.reason_md}</div>}
      {record.work_content_md && <div>② 工作内容 / work：{record.work_content_md}</div>}
      {record.work_qty_md && <div>② 数量 / qty：{record.work_qty_md}</div>}
      {(record.persons ?? []).length > 0 && (
        <div>③ 人员 / crew：{record.persons!.map((p) => `${p.name}${p.trade ? `（${p.trade}${p.grade ? ` ${p.grade}` : ''}）` : ''} ${p.hours ?? '—'}h`).join('、')}</div>
      )}
      {(record.materials ?? []).length > 0 && (
        <div>④ 材料 / materials：{record.materials!.map((m) => `${m.name} ${m.qty ?? ''}${m.unit ?? ''}`).join('、')}</div>
      )}
      {(record.equipment ?? []).length > 0 && (
        <div>⑤ 机械 / equipment：{record.equipment!.map((q) => `${q.model} ×${q.qty ?? ''}${q.shifts ? ` ${q.shifts}台班` : ''}`).join('、')}</div>
      )}
      {record.other_vouchers_md && <div>⑥ 其他凭证 / vouchers：{record.other_vouchers_md}</div>}
      {record.submitted_at && <div>上报时间 / filed：{record.submitted_at.slice(0, 16).replace('T', ' ')}</div>}
      {record.verified_at && <div>核实时间 / verified：{record.verified_at.slice(0, 16).replace('T', ' ')}</div>}
    </div>
  );
}
